import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, Loader2, Flag, Mail, Check, RefreshCw, EyeOff, Inbox, ShieldOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    adminMe, adminListReports, adminListMessages,
    adminUpdateReportStatus, adminUpdateMessageStatus,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_STYLES = {
    open: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    new: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    in_review: "bg-blue-500/15 text-blue-600 border-blue-500/30",
    resolved: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    dismissed: "bg-muted text-muted-foreground border-border/60",
};

const REPORT_STATUSES = ["open", "in_review", "resolved", "dismissed"];
const MESSAGE_STATUSES = ["new", "in_review", "resolved", "dismissed"];

export default function AdminInbox() {
    const nav = useNavigate();
    const { user, loading } = useAuth();
    const [isAdmin, setIsAdmin] = useState(null);
    const [reports, setReports] = useState([]);
    const [messages, setMessages] = useState([]);
    const [busy, setBusy] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const [rep, msg] = await Promise.all([adminListReports(), adminListMessages()]);
            setReports(rep);
            setMessages(msg);
        } catch {
            toast.error("Failed to load admin queues");
        }
        setRefreshing(false);
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!user) { nav("/"); return; }
        (async () => {
            setBusy(true);
            try {
                await adminMe();
                setIsAdmin(true);
                await load();
            } catch (e) {
                setIsAdmin(false);
            }
            setBusy(false);
        })();
    }, [user, loading, nav, load]);

    async function changeReportStatus(id, status) {
        try {
            const updated = await adminUpdateReportStatus(id, status);
            setReports(rs => rs.map(r => r.id === id ? updated : r));
            toast.success(`Report → ${status.replace("_", " ")}`);
        } catch {
            toast.error("Update failed");
        }
    }

    async function changeMessageStatus(id, status) {
        try {
            const updated = await adminUpdateMessageStatus(id, status);
            setMessages(ms => ms.map(m => m.id === id ? updated : m));
            toast.success(`Message → ${status.replace("_", " ")}`);
        } catch {
            toast.error("Update failed");
        }
    }

    if (loading || busy) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    if (isAdmin === false) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background text-center px-6">
                <ShieldOff className="w-10 h-10 text-muted-foreground" />
                <div>
                    <h1 className="font-heading text-2xl">Admins only</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Sign in with the account listed as <code>ADMIN_EMAIL</code> on the server.
                    </p>
                </div>
                <Button data-testid="admin-back-home" variant="outline" className="rounded-full" onClick={() => nav("/")}>
                    <ArrowLeft className="w-4 h-4" /> Back to dashboard
                </Button>
            </div>
        );
    }

    const openReports = reports.filter(r => r.status === "open").length;
    const newMessages = messages.filter(m => m.status === "new").length;

    return (
        <div className="min-h-screen bg-background">
            <header className="flex items-center justify-between px-6 h-14 border-b border-border/60 bg-card/50 backdrop-blur">
                <div className="flex items-center gap-3">
                    <Button data-testid="admin-back-button" variant="ghost" size="sm" onClick={() => nav("/")}>
                        <ArrowLeft className="w-4 h-4" /> Dashboard
                    </Button>
                    <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
                        <Inbox className="w-3.5 h-3.5" /> Admin inbox
                    </div>
                </div>
                <Button data-testid="admin-refresh" variant="ghost" size="sm" onClick={load} disabled={refreshing}>
                    {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Refresh
                </Button>
            </header>

            <main className="max-w-4xl mx-auto p-6">
                <Tabs defaultValue="reports">
                    <TabsList className="grid grid-cols-2 w-full max-w-md">
                        <TabsTrigger data-testid="admin-tab-reports" value="reports" className="flex items-center gap-2">
                            <Flag className="w-3.5 h-3.5" /> Reports
                            {openReports > 0 && <Badge>{openReports}</Badge>}
                        </TabsTrigger>
                        <TabsTrigger data-testid="admin-tab-messages" value="messages" className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5" /> Messages
                            {newMessages > 0 && <Badge>{newMessages}</Badge>}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="reports" className="mt-4 space-y-3" data-testid="admin-reports-panel">
                        {reports.length === 0 && <EmptyState label="No AI reports yet." />}
                        {reports.map(r => (
                            <ReportCard
                                key={r.id}
                                report={r}
                                onChange={(s) => changeReportStatus(r.id, s)}
                            />
                        ))}
                    </TabsContent>

                    <TabsContent value="messages" className="mt-4 space-y-3" data-testid="admin-messages-panel">
                        {messages.length === 0 && <EmptyState label="No contact messages yet." />}
                        {messages.map(m => (
                            <MessageCard
                                key={m.id}
                                message={m}
                                onChange={(s) => changeMessageStatus(m.id, s)}
                            />
                        ))}
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

function Badge({ children }) {
    return (
        <span className="min-w-[18px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center tabular-nums">
            {children}
        </span>
    );
}

function StatusPill({ status }) {
    return (
        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.dismissed}`}>
            {status.replace("_", " ")}
        </span>
    );
}

function EmptyState({ label }) {
    return (
        <div className="text-center py-16 text-sm text-muted-foreground italic">{label}</div>
    );
}

function ReportCard({ report, onChange }) {
    const resolved = report.status === "resolved" || report.status === "dismissed";
    return (
        <Card data-testid={`admin-report-${report.id}`} className={`p-4 ${resolved ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Flag className="w-3.5 h-3.5 text-destructive" />
                        <span className="text-sm font-medium">{report.category}</span>
                        <StatusPill status={report.status} />
                    </div>
                    <p className="text-sm mt-2 leading-relaxed">{report.description}</p>
                    {report.excerpt && (
                        <blockquote className="mt-2 pl-3 border-l-2 border-border/60 text-xs text-muted-foreground italic whitespace-pre-wrap">
                            {report.excerpt}
                        </blockquote>
                    )}
                    <div className="mt-2 text-[11px] text-muted-foreground">
                        {report.user_email} · {new Date(report.created_at).toLocaleString()}
                    </div>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-1 flex-wrap">
                {report.status !== "resolved" && (
                    <Button data-testid={`admin-report-resolve-${report.id}`} size="sm" className="h-7 text-xs rounded-full"
                        onClick={() => onChange("resolved")}>
                        <Check className="w-3 h-3" /> Mark resolved
                    </Button>
                )}
                {report.status !== "in_review" && report.status !== "resolved" && (
                    <Button size="sm" variant="secondary" className="h-7 text-xs rounded-full"
                        onClick={() => onChange("in_review")}>
                        In review
                    </Button>
                )}
                {report.status !== "dismissed" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-muted-foreground"
                        onClick={() => onChange("dismissed")}>
                        <EyeOff className="w-3 h-3" /> Dismiss
                    </Button>
                )}
                {report.status !== "open" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-muted-foreground"
                        onClick={() => onChange("open")}>
                        Reopen
                    </Button>
                )}
            </div>
        </Card>
    );
}

function MessageCard({ message, onChange }) {
    const resolved = message.status === "resolved" || message.status === "dismissed";
    return (
        <Card data-testid={`admin-message-${message.id}`} className={`p-4 ${resolved ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Mail className="w-3.5 h-3.5 text-primary" />
                        <span className="text-sm font-medium">{message.name}</span>
                        <a href={`mailto:${message.email}`} className="text-xs text-primary hover:underline break-all">
                            {message.email}
                        </a>
                        <StatusPill status={message.status} />
                    </div>
                    <p className="text-sm mt-2 leading-relaxed whitespace-pre-wrap">{message.message}</p>
                    <div className="mt-2 text-[11px] text-muted-foreground">
                        {new Date(message.created_at).toLocaleString()}
                    </div>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-1 flex-wrap">
                {message.status !== "resolved" && (
                    <Button data-testid={`admin-message-resolve-${message.id}`} size="sm" className="h-7 text-xs rounded-full"
                        onClick={() => onChange("resolved")}>
                        <Check className="w-3 h-3" /> Mark resolved
                    </Button>
                )}
                {message.status !== "in_review" && message.status !== "resolved" && (
                    <Button size="sm" variant="secondary" className="h-7 text-xs rounded-full"
                        onClick={() => onChange("in_review")}>
                        In review
                    </Button>
                )}
                {message.status !== "dismissed" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-muted-foreground"
                        onClick={() => onChange("dismissed")}>
                        <EyeOff className="w-3 h-3" /> Dismiss
                    </Button>
                )}
                {message.status !== "new" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-muted-foreground"
                        onClick={() => onChange("new")}>
                        Mark new
                    </Button>
                )}
            </div>
        </Card>
    );
}
